import type { Handler, NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';
import { camelize, dasherize, pluralize, singularize } from '@warp-drive/utilities/string';

/**
 * Normalizes JSON:API resource and linkage types to dasherized singular schema names before
 * caching. Attribute and relationship keys become camelCase only for dasherized responses,
 * including target-contract responses that ignore key-format negotiation.
 * Non-JSON:API payloads pass through unchanged.
 */

type JsonApiResourceish = {
  type?: unknown;
  relationships?: Record<string, { data?: unknown }>;
};

function normalizeType(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) return value;
  return singularize(dasherize(value));
}

function camelizeKeys(
  obj: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!obj || typeof obj !== 'object') return obj;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    out[camelize(key)] = value;
  }
  return out;
}

function normalizeResource(resource: unknown, withKeyCamelization = false): void {
  if (!resource || typeof resource !== 'object') return;
  const r = resource as JsonApiResourceish & { attributes?: Record<string, unknown> };
  if ('type' in r) {
    r.type = normalizeType(r.type);
  }
  if (withKeyCamelization) {
    // Headerless apps get DASHERIZED wire keys; schema fields are camelCase.
    // The legacy serializer camelized during normalize — builder responses
    // get the same treatment here so cache inserts match schema fields.
    if (r.attributes) r.attributes = camelizeKeys(r.attributes);
    if (r.relationships) {
      r.relationships = camelizeKeys(r.relationships) as typeof r.relationships;
    }
  }
  if (r.relationships && typeof r.relationships === 'object') {
    for (const rel of Object.values(r.relationships)) {
      const linkage = rel?.data;
      if (Array.isArray(linkage)) {
        linkage.forEach((l) => normalizeResource(l, false));
      } else if (linkage) {
        normalizeResource(linkage, false);
      }
    }
  }
}

export function normalizeJsonApiTypes<T>(content: T, withKeyCamelization = false): T {
  if (!content || typeof content !== 'object') return content;
  const doc = content as { data?: unknown; included?: unknown[] };
  // Only treat it as JSON:API when a `data` member exists — custom payloads
  // (search, aggregate) stay untouched.
  if (!('data' in doc)) return content;
  const data = doc.data;
  if (Array.isArray(data)) {
    data.forEach((r) => normalizeResource(r, withKeyCamelization));
  } else if (data) {
    normalizeResource(data, withKeyCamelization);
  }
  if (Array.isArray(doc.included)) {
    doc.included.forEach((r) => normalizeResource(r, withKeyCamelization));
  }
  return content;
}

/**
 * Request-body mirror for MUTATIONS (createRecord/updateRecord builders):
 * cache-serialized bodies carry SCHEMA-format types (singular-dasherized)
 * and camelCase attribute keys. The API expects pluralized types and keys
 * in the negotiated format (camelized under the header, dasherized
 * without — exactly what the legacy JSONAPISerializer emitted). Touches
 * only bodies that parse to a JSON:API document (`data.type` present);
 * custom builder bodies pass through untouched.
 */
function wireType(value: unknown, camelized: boolean): unknown {
  if (typeof value !== 'string' || !value) return value;
  return camelized ? pluralize(camelize(value)) : pluralize(dasherize(value));
}

function dasherizeKeys(
  obj: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!obj || typeof obj !== 'object') return obj;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    out[dasherize(key)] = value;
  }
  return out;
}

function serializeResourceForWire(
  resource: unknown,
  camelized: boolean,
  targetContract = false
): void {
  if (!resource || typeof resource !== 'object') return;
  const r = resource as JsonApiResourceish & {
    attributes?: Record<string, unknown>;
    id?: unknown;
    lid?: unknown;
  };
  if ('type' in r) {
    r.type = wireType(r.type, targetContract ? false : camelized);
  }
  // Cache bookkeeping, not API contract (the legacy serializer sent
  // neither): client lid never goes on the wire; id only when real.
  if ('lid' in r) delete r.lid;
  if ('id' in r && r.id === null) delete r.id;
  if (!camelized) {
    if (r.attributes) r.attributes = dasherizeKeys(r.attributes);
    if (r.relationships) {
      r.relationships = dasherizeKeys(r.relationships) as typeof r.relationships;
    }
  }
  if (r.relationships && typeof r.relationships === 'object') {
    const rels = r.relationships as Record<string, { data?: unknown } | undefined>;
    for (const [name, rel] of Object.entries(rels)) {
      const linkage = rel?.data;
      // Normalize to a list so to-one (object) and to-many (array) share the
      // same cleanup. `data: null` (an intentional to-one clear) yields no
      // items and is left untouched.
      const items = Array.isArray(linkage) ? linkage : linkage ? [linkage] : [];

      // A linkage whose `id` is null points at an UNSAVED related record:
      // `serializePatch` emits `{ type, id: null, lid }` for it, which both
      // leaks the client-only `lid` and is rejected by jsonapi-resources as an
      // invalid links object (error 115) — a linkage must be exactly
      // `{ type, id }`. An unsaved record has no server identity to reference,
      // so drop the whole relationship rather than send a malformed (or, once
      // `lid` is stripped, relationship-clearing) member.
      const hasUnsavedRef = items.some(
        (l) =>
          l && typeof l === 'object' && 'id' in (l as object) && (l as { id?: unknown }).id === null
      );
      if (hasUnsavedRef) {
        delete rels[name];
        continue;
      }

      for (const l of items) {
        if (!l || typeof l !== 'object') continue;
        const item = l as { type?: unknown; lid?: unknown };
        if ('type' in item) item.type = wireType(item.type, targetContract ? false : camelized);
        // Cache bookkeeping, not API contract: strip the client lid from
        // linkages too, mirroring the top-level resource above.
        if ('lid' in item) delete item.lid;
      }
    }
    if (Object.keys(rels).length === 0) delete (r as { relationships?: unknown }).relationships;
  }
}

export function serializeBodyForWire(
  body: string,
  camelized: boolean,
  targetContract = false
): string {
  let doc: unknown;
  try {
    doc = JSON.parse(body);
  } catch {
    return body;
  }
  if (!doc || typeof doc !== 'object') return body;
  const d = doc as { data?: unknown };
  if (!d.data || typeof d.data !== 'object' || !('type' in (d.data as object))) return body;
  serializeResourceForWire(d.data, camelized, targetContract);
  return JSON.stringify(doc);
}

/**
 * Request-side mirror: when the negotiated key format is camelized, the API
 * also expects CAMELIZED include paths (`emailAddresses`, not
 * `email-addresses` — dasherized multi-word paths get a 400). Call sites keep
 * writing schema-format (dasherized) paths; this rewrites each dot-segment
 * of the `include` param on the way out.
 */
function camelizeIncludeParam(url: string): string {
  const parsed = new URL(url, 'http://_');
  const include = parsed.searchParams.get('include');
  if (!include) return url;
  const camelized = include
    .split(',')
    .map((path) =>
      path
        .split('.')
        .map((segment) => camelize(segment))
        .join('.')
    )
    .join(',');
  if (camelized === include) return url;
  parsed.searchParams.set('include', camelized);
  return url.startsWith('http') ? parsed.toString() : parsed.pathname + parsed.search;
}

/**
 * Request-side mirror for SPARSE FIELDSETS: when the negotiated key format is
 * camelized, the API validates each `fields[<type>]` member name against the
 * CAMELIZED resource attribute names. Dasherized members (`created-at`,
 * `is-global`) get a 400 ("created-at is not a valid field for user."). Call
 * sites keep writing schema-format (dasherized) member names; this camelizes
 * each comma-separated VALUE of every `fields[...]` param on the way out.
 *
 * Only the VALUES (attribute names) are rewritten — the `fields[<type>]` key
 * (resource type) is left as-is (the API matches it by route/type format, not
 * the key formatter; `fields[user]`/`fields[company-settings]` resolve fine).
 * `sort` and `filter` are intentionally untouched: the API tolerates
 * dasherized sort field names and matches filter keys by their declared
 * (dasherized) names — both verified against the live API under camelized
 * negotiation. Mirrors the legacy serializer's key-formatting half.
 */
function camelizeFieldsParams(url: string): string {
  const parsed = new URL(url, 'http://_');
  const fieldKeys: string[] = [];
  parsed.searchParams.forEach((_value, key) => {
    if (key.startsWith('fields[')) fieldKeys.push(key);
  });
  if (fieldKeys.length === 0) return url;
  let changed = false;
  for (const key of fieldKeys) {
    const value = parsed.searchParams.get(key);
    if (!value) continue;
    const camelized = value
      .split(',')
      .map((member) => camelize(member.trim()))
      .join(',');
    if (camelized !== value) {
      parsed.searchParams.set(key, camelized);
      changed = true;
    }
  }
  if (!changed) return url;
  return url.startsWith('http') ? parsed.toString() : parsed.pathname + parsed.search;
}

/**
 * EmberData's store.query / store.findRecord DROPPED query params whose value
 * was `undefined` (the request serializer skipped them). WarpDrive's builders
 * serialize them as the literal string `"undefined"`/`"NaN"` instead, so a
 * call site that spreads a possibly-undefined value (e.g. `{ sort }` where the
 * `sort` query param has no default) now sends `sort=undefined` and the API
 * 400s ("undefined is not a valid sort criteria"). Restore the legacy
 * drop-undefined behavior here so the wire matches main. A genuinely-undefined
 * value never carries meaning on the wire; intentional empty/null params are
 * left untouched.
 */
function stripUndefinedParams(url: string): string {
  const parsed = new URL(url, 'http://_');
  const drop: string[] = [];
  parsed.searchParams.forEach((value, key) => {
    if (value === 'undefined' || value === 'NaN') drop.push(key);
  });
  if (drop.length === 0) return url;
  for (const key of drop) parsed.searchParams.delete(key);
  return url.startsWith('http') ? parsed.toString() : parsed.pathname + parsed.search;
}

/**
 * The tio-api v2 kit tags its contract on the wire. A builder that asks for the
 * target contract sends this header, and the kit's negotiation reads it.
 */
export const CONTRACT_HEADER = 'X-Tio-Contract';
const TARGET_CONTRACT = 2;

/**
 * Target-contract requests preserve builder query names. The kit reads include and sparse fields
 * directly; legacy camelization would silently select the wrong fields. Requests without the header
 * retain legacy rewrites.
 */
function declaresTargetContract(headers: Headers | HeadersInit | undefined): boolean {
  if (!headers) return false;
  const value =
    headers instanceof Headers
      ? headers.get(CONTRACT_HEADER)
      : new Headers(headers as HeadersInit).get(CONTRACT_HEADER);

  return Number(value) >= TARGET_CONTRACT;
}

export class TioTypeNormalizationHandler implements Handler {
  constructor(private jsonApiKeyFormat?: string) {}

  async request<T>(context: RequestContext, next: NextFn<T>) {
    let request = context.request;
    const camelized = this.jsonApiKeyFormat === 'camelized';
    const targetContract = declaresTargetContract(
      (request as { headers?: Headers | HeadersInit }).headers
    );
    if (request.url) {
      // Always (key-format independent): drop params serialized as the literal
      // "undefined"/"NaN" — EmberData omitted undefined query params; the
      // builder doesn't, so e.g. `sort=undefined` -> 400. Then, under camelized
      // negotiation, camelize include paths + sparse-fieldset member values.
      let url = stripUndefinedParams(request.url);
      if (camelized && !targetContract) {
        url = camelizeFieldsParams(camelizeIncludeParam(url));
      }
      if (url !== request.url) {
        request = { ...request, url };
      }
    }
    if (typeof request.body === 'string' && request.body) {
      // The kit accepts the negotiated member names, but requires JSON:API
      // resource types to stay dasherized even for camelized admin clients.
      const body = serializeBodyForWire(request.body, camelized, targetContract);
      if (body !== request.body) {
        request = { ...request, body };
      }
    }
    const result = await next(request);
    // Headerless apps receive dasherized wire keys — camelize on the way
    // into the cache (the legacy serializer's normalize half). Target-contract
    // responses are always dasherized (the kit ignores x-json-api-key-format),
    // so they camelize under a camelized app too; without this, kit attributes
    // miss the camelCase schema fields and read as undefined.
    normalizeJsonApiTypes(result.content, !camelized || targetContract);
    return result;
  }
}
